(() => {
  const HOST_ID = "sfSizeGuideHost";
  const DEFAULT_VIEW_NAME = "sf-size-guide";

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  let lastTrigger = null;
  let activeRoot = null;
  let activeDialog = null;
  let closeTimer = 0;

  const guideCache = new Map();

  function getHost() {
    return qs(`#${HOST_ID}`);
  }

  function getViewName() {
    const host = getHost();
    return (host && host.getAttribute("data-sf-sg-view")) || DEFAULT_VIEW_NAME;
  }

  function getFocusable(dialog) {
    if (!dialog) return [];

    const els = qsa(
      [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(","),
      dialog
    );

    return els.filter((el) => {
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
  }

  function onDialogKeydown(e) {
    if (!activeDialog) return;

    if (e.key === "Escape") {
      e.preventDefault();
      closeDrawer(activeRoot);
      return;
    }

    if (e.key !== "Tab") return;

    const focusables = getFocusable(activeDialog);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === activeDialog) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function cleanupActiveDialog() {
    if (activeDialog) {
      activeDialog.removeEventListener("keydown", onDialogKeydown);
    }
    activeRoot = null;
    activeDialog = null;
  }

  function closeDrawer(root) {
    if (!root) return;

    root.removeAttribute("data-sf-open");
    root.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";

    cleanupActiveDialog();

    clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (!root.hasAttribute("data-sf-open")) {
        root.hidden = true;
      }
    }, 220);

    if (lastTrigger && typeof lastTrigger.focus === "function") {
      try {
        lastTrigger.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  function openDrawer(root) {
    if (!root) return;

    clearTimeout(closeTimer);

    root.hidden = false;
    root.setAttribute("aria-hidden", "false");
    root.removeAttribute("data-sf-open");

    document.documentElement.style.overflow = "hidden";

    const dialog = qs('[role="dialog"]', root);
    activeRoot = root;
    activeDialog = dialog || null;

    requestAnimationFrame(() => {
      root.setAttribute("data-sf-open", "true");

      if (activeDialog) {
        activeDialog.addEventListener("keydown", onDialogKeydown);
        try {
          activeDialog.focus({ preventScroll: true });
        } catch (e) {}
      }

      if (typeof root.__sfSgSyncBar === "function") {
        root.__sfSgSyncBar();
      }
    });
  }

function applyUnit(root, unit) {
  // Active IN / CM button
  qsa("[data-sf-sg-unit]", root).forEach((btn) => {
    btn.classList.toggle(
      "is-active",
      btn.getAttribute("data-sf-sg-unit") === unit
    );
  });

  // Original Size Guide table
  qsa("[data-sf-sg-chart-unit]", root).forEach((block) => {
    block.hidden =
      block.getAttribute("data-sf-sg-chart-unit") !== unit;
  });

  // NEW: Product Measurements table
  qsa("[data-sf-sg-product-chart-unit]", root).forEach((block) => {
    block.hidden =
      block.getAttribute("data-sf-sg-product-chart-unit") !== unit;
  });

  // Re-sync all custom table scrollbars
  if (typeof root.__sfSgSyncBar === "function") {
    requestAnimationFrame(root.__sfSgSyncBar);
  }
}

function setTab(root, tabName) {
  // Active tab button
  qsa("[data-sf-sg-tab]", root).forEach((btn) => {
    btn.classList.toggle(
      "is-active",
      btn.getAttribute("data-sf-sg-tab") === tabName
    );
  });

  // Active tab panel
  qsa("[data-sf-sg-panel]", root).forEach((panel) => {
    panel.classList.toggle(
      "is-active",
      panel.getAttribute("data-sf-sg-panel") === tabName
    );
  });

  // Switch the short description
  qsa("[data-sf-sg-desc]", root).forEach((desc) => {
    desc.hidden =
      desc.getAttribute("data-sf-sg-desc") !== tabName;
  });

  // IN / CM is only needed for Size Guide + Product Measurements
  const unitControls = qs(".sf-sg__unit", root);

  if (unitControls) {
    unitControls.hidden = tabName === "measure";
  }

  // Re-sync scrollbar after changing tab
  if (typeof root.__sfSgSyncBar === "function") {
    requestAnimationFrame(root.__sfSgSyncBar);
  }
}

function initChartScrollBar(root) {
  const syncFunctions = [];

  qsa("[data-sf-sg-panel]", root).forEach((panel) => {
    const wrap = qs(".sf-sg__tableWrap", panel);
    const bar = qs("[data-sf-sg-scrollbar]", panel);
    const thumb = qs("[data-sf-sg-scrollbar-thumb]", panel);

    if (!wrap || !bar || !thumb) return;

    if (wrap.__sfSgBarInit) return;
    wrap.__sfSgBarInit = true;

    let raf = 0;

    function sync() {
      raf = 0;

      const scrollW = wrap.scrollWidth;
      const clientW = wrap.clientWidth;

      if (!scrollW || scrollW <= clientW + 1) {
        bar.style.display = "none";
        thumb.style.width = "0px";
        thumb.style.transform = "translateX(0px)";
        return;
      }

      bar.style.display = "";

      const ratio = clientW / scrollW;

      const thumbW = Math.max(
        20,
        Math.round(bar.clientWidth * ratio)
      );

      const maxLeft = Math.max(
        0,
        bar.clientWidth - thumbW
      );

      const maxScroll = scrollW - clientW;

      const scrollRatio = maxScroll
        ? wrap.scrollLeft / maxScroll
        : 0;

      const left = Math.round(
        maxLeft * scrollRatio
      );

      thumb.style.width = thumbW + "px";
      thumb.style.transform =
        "translateX(" + left + "px)";
    }

    function requestSync() {
      if (raf) return;

      raf = requestAnimationFrame(sync);
    }

    wrap.addEventListener(
      "scroll",
      requestSync,
      { passive: true }
    );

    if (window.ResizeObserver) {
      const ro = new ResizeObserver(requestSync);

      ro.observe(wrap);
      ro.observe(bar);

      if (!root.__sfSgResizeObservers) {
        root.__sfSgResizeObservers = [];
      }

      root.__sfSgResizeObservers.push(ro);
    }

    syncFunctions.push(requestSync);
  });

  function syncAll() {
    syncFunctions.forEach((sync) => {
      sync();
    });
  }

  let resizeRaf = 0;

  function onResize() {
    if (resizeRaf) return;

    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      syncAll();
    });
  }

  window.addEventListener(
    "resize",
    onResize,
    { passive: true }
  );

  root.__sfSgSyncBar = syncAll;

  syncAll();
}

  function bindInteractions(root) {
    if (root.__sfSgBound) return;
    root.__sfSgBound = true;

    root.addEventListener("click", (e) => {
      const closeBtn = e.target.closest("[data-sf-sg-close]");
      if (closeBtn) {
        closeDrawer(root);
        return;
      }

      const tabBtn = e.target.closest("[data-sf-sg-tab]");
      if (tabBtn) {
        setTab(root, tabBtn.getAttribute("data-sf-sg-tab"));
        return;
      }

      const unitBtn = e.target.closest("[data-sf-sg-unit]");
      if (unitBtn) {
        applyUnit(root, unitBtn.getAttribute("data-sf-sg-unit"));
      }
    });
initChartScrollBar(root);
setTab(root, "guide");
applyUnit(root, "in");
  }

  async function loadGuide(handle) {
    const host = getHost();
    if (!host || !handle) return;

    const view = getViewName();
    const cacheKey = `${handle}::${view}`;

    let html = guideCache.get(cacheKey);

    if (!html) {
      const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return;
      html = await res.text();
      guideCache.set(cacheKey, html);
    }

    host.innerHTML = html;

    const root = qs("[data-sf-size-guide-root]", host);
    if (!root) return;

    bindInteractions(root);
    openDrawer(root);
  }

  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest("[data-sf-size-guide]");
      if (!btn) return;

      const handle = btn.getAttribute("data-product-handle");
      if (!handle) return;

      lastTrigger = btn;
      e.preventDefault();
      loadGuide(handle);
    },
    true
  );

  window.SFSizeGuide = window.SFSizeGuide || {};
  window.SFSizeGuide.open = function (handle) {
    return loadGuide(handle);
  };
  window.SFSizeGuide.close = function () {
    const host = getHost();
    const root = host ? qs("[data-sf-size-guide-root]", host) : null;
    closeDrawer(root);
  };
})();